import moment, { Moment } from 'moment'
import { DateRestriction } from '../types'

/**
 * Проверяет, является ли дата заблокированной на основе типа ограничения
 */
export const isDateDisabled = (
	date: Moment,
	restrictionType?: DateRestriction,
	daysRangeDisabled?: number,
): boolean => {
	if (!date) return false

	const now = moment()

	// Ограничение только прошлыми датами
	if (restrictionType === DateRestriction.PAST) {
		// Если задан диапазон дней
		if (daysRangeDisabled) {
			const daysAgo = moment()
				.subtract(daysRangeDisabled, 'days')
				.startOf('day')
			return date.isBefore(daysAgo) || date.isAfter(now.endOf('day'))
		}
		// Блокируем все даты после сегодня
		return date.isAfter(now.endOf('day'))
	}

	// Ограничение только будущими датами
	if (restrictionType === DateRestriction.FUTURE) {
		// Если задан диапазон дней
		if (daysRangeDisabled) {
			const daysAhead = moment().add(daysRangeDisabled, 'days').endOf('day')
			return date.isAfter(daysAhead) || date.isBefore(now.startOf('day'))
		}
		// Блокируем все даты до сегодня
		return date.isBefore(now.startOf('day'))
	}

	return false
}

/**
 * Проверяет, находится ли дата в заблокированном диапазоне
 */
export const isDateInBlockedRange = (
	date: Moment,
	blockedRange?: [Moment, Moment],
): boolean => {
	if (!blockedRange) return false

	const [start, end] = blockedRange
	return date.isBetween(start, end, 'day', '[]')
}

/**
 * Проверяет, находится ли дата в разрешенном диапазоне для выбора
 */
export const isDateInAllowedRange = (
	date: Moment,
	allowedRange?: { firstItem: number; lastItem: number },
): boolean => {
	if (!allowedRange) return true

	const { firstItem, lastItem } = allowedRange
	const dateTimestamp = date.valueOf()

	return (
		dateTimestamp >= moment(firstItem).valueOf() &&
		dateTimestamp <= moment(lastItem).valueOf()
	)
}

/**
 * Комплексная проверка доступности даты с учетом всех ограничений
 */
export const isDateAvailable = (
	date: Moment,
	options: {
		restrictionType?: DateRestriction
		daysRangeDisabled?: number
		blockedDatesRange?: [Moment, Moment]
		disabledDates?: { firstItem: number; lastItem: number }
	},
): boolean => {
	const {
		restrictionType,
		daysRangeDisabled,
		blockedDatesRange,
		disabledDates,
	} = options

	// Проверка на заблокированный диапазон
	if (isDateInBlockedRange(date, blockedDatesRange)) {
		return false
	}

	// Проверка на разрешенный диапазон
	if (disabledDates && !isDateInAllowedRange(date, disabledDates)) {
		return false
	}

	// Проверка на тип ограничения
	if (isDateDisabled(date, restrictionType, daysRangeDisabled)) {
		return false
	}

	return true
}

/**
 * Проверяет, является ли дата сегодняшним днем
 */
export const isToday = (date: Moment): boolean => {
	return date.isSame(moment(), 'day')
}

/**
 * Проверяет, является ли дата вчерашним днем
 */
export const isYesterday = (date: Moment): boolean => {
	return date.isSame(moment().subtract(1, 'day'), 'day')
}

/**
 * Проверяет, принадлежит ли дата текущему месяцу
 */
export const isCurrentMonth = (date: Moment, currentMonth: Moment): boolean => {
	return date.isSame(currentMonth, 'month')
}

/**
 * Проверяет, находится ли дата в диапазоне
 */
export const isDateInRange = (
	date: Moment,
	startDate: Moment | null,
	endDate: Moment | null,
): boolean => {
	if (!startDate || !endDate) return false
	return date.isBetween(startDate, endDate, 'day', '[]')
}

/**
 * Проверяет, является ли дата началом или концом диапазона
 */
export const isRangeBoundary = (
	date: Moment,
	startDate: Moment | null,
	endDate: Moment | null,
): { isStart: boolean; isEnd: boolean } => {
	return {
		isStart: startDate ? date.isSame(startDate, 'day') : false,
		isEnd: endDate ? date.isSame(endDate, 'day') : false,
	}
}
